import { useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, MessageCircle, Download, X } from "lucide-react";
import { toast } from "sonner";

/**
 * QrShareDialog — dialog réutilisable pour partager une URL avec QR + WhatsApp + PNG.
 *
 * Props:
 *   open, onClose        — contrôle
 *   title                — titre du dialog (ex : "Partager la Porsche 911")
 *   subtitle             — sous-titre (optionnel)
 *   url                  — URL absolue à encoder
 *   waMessage            — message pré-rédigé pour WhatsApp
 *   downloadName         — nom du fichier PNG téléchargé (sans extension)
 */
export default function QrShareDialog({ open, onClose, title, subtitle, url, waMessage, downloadName = "kv-share" }) {
  const svgId = `qr-share-${downloadName}`;

  useEffect(() => {
    // reset scroll when opening
    if (open) setTimeout(() => window.scrollTo(0, window.scrollY), 0);
  }, [open]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const shareWA = () => {
    const txt = `${waMessage || ""}\n\n${url}`.trim();
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener");
  };

  const download = () => {
    const svg = document.getElementById(svgId);
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const urlObj = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 800; canvas.height = 800;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 800, 800);
      ctx.drawImage(img, 40, 40, 720, 720);
      const png = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = png; a.download = `${downloadName}.png`;
      a.click();
      URL.revokeObjectURL(urlObj);
    };
    img.src = urlObj;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="qr-share-dialog" className="bg-[#1E1E1E] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white">Partager</DialogTitle>
          <DialogDescription className="text-white/50 text-xs">Scanner le QR ou partager le lien direct.</DialogDescription>
        </DialogHeader>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A75A]">{title}</p>
        {subtitle && <p className="text-white/60 text-sm mt-1">{subtitle}</p>}

        <div className="mt-6 mx-auto w-fit bg-white p-4 rounded-md">
          <QRCodeSVG
            id={svgId}
            value={url}
            size={240}
            level="M"
            bgColor="#ffffff"
            fgColor="#0D0D0D"
            imageSettings={{ src: "/assets/kv-logo.png", height: 40, width: 40, excavate: true }}
          />
        </div>

        <div className="mt-6 bg-[#0D0D0D] border border-white/10 rounded-md p-3 flex items-center gap-2">
          <input readOnly value={url} data-testid="qr-share-url" className="flex-1 bg-transparent text-white/70 text-xs outline-none truncate" />
          <button onClick={copy} data-testid="qr-share-copy" title="Copier le lien" className="text-[#C8A75A] hover:text-white p-1"><Copy className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <button
            data-testid="qr-share-wa"
            onClick={shareWA}
            className="flex items-center justify-center gap-2 bg-[#25D366] text-black hover:bg-white px-4 py-3 rounded-md text-xs uppercase tracking-widest font-medium"
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </button>
          <button
            data-testid="qr-share-download"
            onClick={download}
            className="flex items-center justify-center gap-2 bg-[#C8A75A] text-black hover:bg-white px-4 py-3 rounded-md text-xs uppercase tracking-widest font-medium"
          >
            <Download className="w-4 h-4" /> Télécharger PNG
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
